// Office invoices page. A live mirror of the Xero invoice cache (Xero stays the
// source of truth). Admins can authorise a DRAFT invoice from here -- the invoice
// approval queue -- which moves it to AUTHORISED in Xero. This is a manual,
// human-driven step (Director policy keeps everything DRAFT by default); nothing
// is auto-authorised.
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { ApiError, api } from "../../lib/api";
import type { InvoiceListItem } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { useAuth } from "../../lib/auth";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { OneOffDraftInvoiceModal } from "./OneOffDraftInvoiceModal";
import { useCustomerFilter, CustomerFilterChip } from "./customerFilter";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import { PageHeader } from "../../components/PageHeader";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { Loader } from "../../components/Loader";
import { StatusPill } from "../../components/StatusPill";
import { formatDate } from "../../lib/format";
import { statusEntry } from "../../lib/statusRegistry";
import { ListToolbar } from "../../components/ListToolbar";
import { useSearchParams } from "react-router-dom";
import { useListSearch } from "../../lib/useListSearch";
import { useServerPage } from "../../lib/useServerPage";
import { Pager } from "../../components/Pager";
import { ExportCsvMenu } from "../../components/ExportCsvMenu";
import { useSortableColumn } from "../../lib/useSortableColumn";
import { SortableHeader } from "../../components/SortableHeader";

const INVOICE_PAGE_SIZE = 50;

type InvoiceStatusFilter = "all" | "overdue" | "paid" | "authorised" | "draft" | "void";
const STATUS_FILTERS: { value: InvoiceStatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "overdue", label: "Overdue" },
  { value: "authorised", label: "Authorised" },
  { value: "paid", label: "Paid" },
  { value: "draft", label: "Draft" },
  { value: "void", label: "Void" },
];

function gbp(n: number): string {
  return `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function InvoicesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Session ADI: when linked from the customer detail page with ?customerId=, show only that
  // customer's invoices (client-side filter over the loaded rows) plus a dismissable chip.
  const { customerId, clear } = useCustomerFilter();
  // Card list below md, table otherwise -- decided once at mount (Session AGZ's mechanism).
  // jsdom has no matchMedia, so every existing test still renders the table it pins.
  const [mobileCards] = useState(initialMobileLayout);

  // The invoice awaiting confirmation, the ids being authorised, the ones just
  // approved this session (shown green), and any per-action error message.
  const [confirming, setConfirming] = useState<InvoiceListItem | null>(null);
  const [approving, setApproving] = useState<Set<string>>(new Set());
  const [justApproved, setJustApproved] = useState<Set<string>>(new Set());
  const [actionError, setActionError] = useState<string | null>(null);

  // Manual "Sync now": pull the latest overdue picture from Xero on demand (the daily
  // cron also runs it at 06:00 UTC). Admin only.
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);

  // Raise a one-off draft invoice (Session AQF, Director decision: here, on the Invoices page).
  // Office and admin alike: /office allows exactly the two roles POST /api/invoices accepts, so the
  // button carries no role test of its own. The new draft lands in the list below, which is how the
  // office sees it worked.
  const [raisingDraft, setRaisingDraft] = useState(false);
  const [draftMsg, setDraftMsg] = useState<string | null>(null);

  // M-4, Session AQT: status filter, search and sort. Session SC2C: all three, and the customer filter, are asked of the SERVER, which answers
  // one page of 50 and the TRUE total of every matching invoice (GET /api/invoices, invoiceListQuery.ts). The search is the one in the address
  // bar (the toolbar writes it there after a short pause).
  const [statusFilter, setStatusFilter] = useState<InvoiceStatusFilter>("all");
  const { search, setSearch } = useListSearch();
  const [searchParams] = useSearchParams();
  const urlSearch = (searchParams.get("q") ?? "").trim();
  const sort = useSortableColumn("invoices");
  const queryKey = JSON.stringify({ statusFilter, customerId, urlSearch, sk: sort.sortKey, sd: sort.sortDir });
  const page = useServerPage(queryKey, (at) =>
    api.invoices.listPage({
      ...(statusFilter !== "all" ? { status: statusFilter } : {}),
      ...(customerId ? { customerId } : {}),
      ...(urlSearch ? { q: urlSearch } : {}),
      ...(sort.sortKey ? { sort: sort.sortKey, dir: sort.sortDir } : {}),
      limit: INVOICE_PAGE_SIZE,
      offset: at,
    }),
  );
  const loading = page.loading;
  const error = page.error;
  const shown: InvoiceListItem[] = page.data?.items ?? [];
  const total = page.data?.total ?? 0;

  async function doApprove(inv: InvoiceListItem) {
    setConfirming(null);
    setActionError(null);
    setApproving((s) => new Set(s).add(inv.id));
    try {
      await api.invoices.approve(inv.id);
      // Session SC2C: the page is re-read, so the row shows the status Xero now holds.
      await page.reload();
      setJustApproved((s) => new Set(s).add(inv.id));
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.status === 503
            ? "Xero is not connected, so this invoice cannot be authorised yet."
            : err.status === 502
              ? "Xero could not authorise this invoice. Open it in Xero to check it has a contact and at least one line."
              : err.status === 409
                ? "This invoice is no longer a draft. Refresh to see its current status."
                : "Could not authorise this invoice. Please try again in a moment."
          : "Could not authorise this invoice. Please try again in a moment.";
      setActionError(msg);
    } finally {
      setApproving((s) => {
        const next = new Set(s);
        next.delete(inv.id);
        return next;
      });
    }
  }

  async function doSync() {
    setSyncing(true);
    setSyncMsg(null);
    setActionError(null);
    try {
      const res = await api.billing.syncInvoices();
      await page.reload();
      setSyncMsg(`Synced. ${res.overdueCount} overdue (${gbp(res.overdueTotal)}).`);
    } catch (err) {
      setActionError(friendlyApiError(err, "Could not sync invoices. Please try again in a moment."));
    } finally {
      setSyncing(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Invoices"
        className="mb-4"
        subtitle={
          <>
            Every invoice raised in Xero.{" "}
            {isAdmin ? "Authorise a draft to send it for payment." : "Only an admin can authorise a draft."}
          </>
        }
        actions={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setDraftMsg(null);
                setActionError(null);
                setRaisingDraft(true);
              }}
            >
              Raise a one-off draft
            </Button>
            {isAdmin && (
              <Button variant="secondary" size="sm" onClick={doSync} disabled={syncing}>
                {syncing ? "Syncing..." : "Sync now"}
              </Button>
            )}
            {/* Session EXP1: the CSV of this list as it is showing (the same status, customer, search and sort), in the More actions menu. */}
            <ExportCsvMenu
              list="invoices"
              params={{
                ...(statusFilter !== "all" ? { status: statusFilter } : {}),
                ...(customerId ? { customerId } : {}),
                ...(urlSearch ? { q: urlSearch } : {}),
                ...(sort.sortKey ? { sort: sort.sortKey, dir: sort.sortDir } : {}),
              }}
            />
          </>
        }
      />
      {/* Transient action results from the header's own buttons -- not part of the page's static
          subtitle, so they sit below the header rather than inside PageHeader's one subtitle line. */}
      {syncMsg && (
        <p role="status" className="mb-3 text-sm text-ele-green">
          {syncMsg}
        </p>
      )}
      {draftMsg && (
        <p role="status" className="mb-3 text-sm text-ele-green">
          {draftMsg}
        </p>
      )}

      {customerId && (
        <div className="mb-3">
          <CustomerFilterChip noun="invoices" onClear={clear} />
        </div>
      )}

      {actionError && (
        <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-ele-surface p-3 text-sm text-ele-strong">
          {actionError}
        </p>
      )}

      {!loading && !error && (
        <ListToolbar
          className="mb-3"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search customer or site..."
          searchLabel="Search invoices"
          filters={STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setStatusFilter(f.value)}
              aria-pressed={statusFilter === f.value}
              className={`rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
                statusFilter === f.value ? "border-ele-green text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-strong"
              }`}
            >
              {f.label}
            </button>
          ))}
        />
      )}

      {loading ? (
        <Loader rows={6} />
      ) : error ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          Could not load the invoices. Please try again in a moment.
        </p>
      ) : shown.length === 0 ? (
        <EmptyState
          message={
            urlSearch
              ? `No invoices match "${urlSearch}".`
              : customerId
                ? "No invoices for this customer."
                : statusFilter !== "all"
                  ? "No invoices match this filter."
                  : "No invoices yet."
          }
        />
      ) : mobileCards ? (
        /* Session AHB mobile card list. The table's three surviving mobile columns divided
           311px between them (customer 144px, due date 54px, status 41px at 375px), so the
           customer clipped, the date read as one word and the status was unreadable -- the
           Director's screenshot. The card stacks instead: the customer name never truncates,
           and the amount and status, both DESKTOP-ONLY columns before this, come back. */
        <MobileCardList label="Invoices">
          {shown.map((inv) => {
            const approved = justApproved.has(inv.id);
            const busy = approving.has(inv.id);
            return (
              <MobileListCard
                key={inv.id}
                to={`/office/invoices/${encodeURIComponent(inv.id)}`}
                primary={inv.customerName}
                secondary={inv.siteName}
                meta={`Due ${formatDate(inv.dueDate)}`}
                right={
                  <>
                    <span className="font-semibold text-ele-strong">{gbp(inv.amount)}</span>
                    {approved ? (
                      <StatusPill word="Approved" tone="green" />
                    ) : (
                      <StatusPill domain="invoice" status={inv.status} />
                    )}
                  </>
                }
                actions={
                  <>
                    {isAdmin && inv.status === "draft" && (
                      <Button size="sm" onClick={() => setConfirming(inv)} disabled={busy} className="flex-1">
                        {busy ? "Approving..." : "Approve"}
                      </Button>
                    )}
                    {/* Icon-only on the card so it never competes with Approve for width; the
                        accessible name carries the full label. */}
                    <a
                      href={`https://go.xero.com/AccountsReceivable/Edit.aspx?InvoiceID=${encodeURIComponent(inv.xeroInvoiceId)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`View ${inv.customerName}'s invoice in Xero`}
                      className="flex min-h-11 min-w-11 items-center justify-center rounded-ele border border-ele-purple-accent text-ele-body transition hover:border-ele-blue hover:text-ele-blue"
                    >
                      <ExternalLink aria-hidden className="h-4 w-4" />
                    </a>
                  </>
                }
              />
            );
          })}
        </MobileCardList>
      ) : (
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            <colgroup>
              <col style={{ width: "26%" }} />
              <col className="max-md:hidden" style={{ width: "22%" }} />
              <col className="max-md:hidden" style={{ width: "12%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "10%" }} />
              <col className="max-md:hidden" style={{ width: "18%" }} />
              {/* Mobile-only disclosure chevron: every row opens the invoice detail page. */}
              <col className="w-8 md:hidden" />
            </colgroup>
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                <SortableHeader label="Customer" colKey="customer" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Site" colKey="site" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Amount" colKey="amount" sort={sort} onToggle={sort.toggle} align="right" className="max-md:hidden" />
                <SortableHeader label="Due" colKey="due" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Status" colKey="status" sort={sort} onToggle={sort.toggle} />
                <th className="cell-actions max-md:hidden px-3 py-2 text-right">Action</th>
                <th className="w-8 cell-actions md:hidden" aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {shown.map((inv) => {
                const approved = justApproved.has(inv.id);
                const busy = approving.has(inv.id);
                return (
                  <tr
                    key={inv.id}
                    className={`group cursor-pointer border-t border-ele-purple-accent ${approved ? "bg-ele-green/10" : ""}`}
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest("a,button,input,select,textarea,label")) return;
                      navigate(`/office/invoices/${encodeURIComponent(inv.id)}`);
                    }}
                  >
                    <td className="px-3 py-2 align-top text-ele-strong">
                      <Link to={`/office/invoices/${encodeURIComponent(inv.id)}`} className="text-ele-green underline-offset-2 hover:underline">
                        {inv.customerName}
                      </Link>
                    </td>
                    <td className="max-md:hidden px-3 py-2 align-top">{inv.siteName ?? "-"}</td>
                    <td className="max-md:hidden px-3 py-2 text-right align-top text-ele-strong">{gbp(inv.amount)}</td>
                    <td className="px-3 py-2 align-top">{formatDate(inv.dueDate)}</td>
                    <td className="px-3 py-2 align-top">
                      {approved ? (
                        <StatusPill word={statusEntry("invoice", inv.status).label} tone="green" />
                      ) : (
                        <StatusPill domain="invoice" status={inv.status} />
                      )}
                    </td>
                    <td className="cell-actions max-md:hidden px-3 py-2 text-right align-top">
                      <div className="flex items-center justify-end gap-2">
                        <a
                          href={`https://go.xero.com/AccountsReceivable/Edit.aspx?InvoiceID=${encodeURIComponent(inv.xeroInvoiceId)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rounded-ele border border-ele-purple-accent px-3 py-1 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-blue hover:text-ele-blue"
                        >
                          View in Xero
                        </a>
                        {isAdmin && inv.status === "draft" && (
                          <Button size="sm" onClick={() => setConfirming(inv)} disabled={busy}>
                            {busy ? "Approving..." : "Approve"}
                          </Button>
                        )}
                        {isAdmin && approved && (
                          <span className="font-menu text-xs uppercase tracking-wide text-ele-green">Approved</span>
                        )}
                      </div>
                    </td>
                    <td className="cell-actions md:hidden pr-1 text-right">
                      <svg
                        aria-hidden
                        viewBox="0 0 16 16"
                        className="ml-auto h-4 w-4 fill-current text-ele-purple transition-colors group-hover:text-ele-green group-focus-within:text-ele-green"
                      >
                        <path d="M6 4l4 4-4 4z" />
                      </svg>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Session SC2C: 50 at a time; the total is the server's count of every invoice matching the filters and search. */}
      {!loading && !error && (
        <div className={page.refreshing ? "opacity-60" : undefined}>
          <Pager offset={page.offset} total={total} shown={shown.length} pageSize={INVOICE_PAGE_SIZE} onOffset={page.setOffset} noun={["invoice", "invoices"]} />
        </div>
      )}

      {raisingDraft && (
        <OneOffDraftInvoiceModal
          onClose={() => setRaisingDraft(false)}
          onCreated={(result) => {
            setRaisingDraft(false);
            setDraftMsg(
              `Draft ${result.invoiceNumber} raised for ${result.customerName}, ${gbp(result.amountExVat)} ex VAT. It is a draft in Xero: nothing has been sent.`,
            );
            // Show it in the list straight away. The route mirrors the draft into the cache, so a
            // re-read is the honest way to display it (the same row the next Xero sync will confirm).
            page
              .reload()
              .catch(() => setActionError("The draft was created, but the list could not be refreshed. Reload the page to see it."));
          }}
        />
      )}

      {confirming && (
        <ModalShell ariaLabel="Authorise this invoice" onClose={() => setConfirming(null)} width="max-w-md" panelClassName="p-6">
            <h2 className="mb-2 font-heading text-xl text-ele-strong">Authorise this invoice?</h2>
            <p className="mb-1 text-sm text-ele-body">
              {confirming.customerName}
              {confirming.siteName ? `, ${confirming.siteName}` : ""}
            </p>
            <p className="mb-4 text-sm text-ele-body">
              This moves <span className="text-ele-strong">{gbp(confirming.amount)}</span> from draft to authorised in Xero.
              Check the figure and any added time or parts in Xero first. This cannot be undone here.
            </p>
            <div className={`gap-2 ${MODAL_ACTIONS}`}>
              <button
                type="button"
                onClick={() => setConfirming(null)}
                className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body ${MODAL_ACTION_BTN}`}
              >
                Cancel
              </button>
              <Button onClick={() => doApprove(confirming)} className={MODAL_ACTION_BTN}>
                Authorise
              </Button>
            </div>
        </ModalShell>
      )}
    </div>
  );
}
